import { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from "@mui/material";
import ConfigUtils from "../../../utils/Config";
import * as constantes from "../../../common/Constantes";
import FacturacionService from "../../../services/FacturacionService";
import type { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";

// Datos que devuelve el servidor para que la pantalla actualice el estado del servicio sin recargar.
export interface AvisoEliminacion {
  message?: string;
  servicioId?: string;
  pacienteId?: string;
  actividadId?: number;
  periodo?: string;
  // Solo al eliminar una rectificativa: la factura original, que vuelve a ser la vigente del servicio.
  facturaVigente?: IFacturaCompleta;
}

interface EliminarFacturaAnuladaProps {
  factura?: { _id: string; estado?: string; numero?: string | null; tipo?: string; facturaOriginalId?: string | null } | null;
  onEliminada: (aviso: AvisoEliminacion) => void;
}

// Botón "Eliminar" (solo administrador) de una factura ANULADA que nunca tuvo número: la limpieza posterior a la
// anulación de un Borrador. Borra la factura y suelta el vínculo con el servicio, que vuelve a poder facturarse.
const EliminarFacturaAnulada = ({ factura, onEliminada }: EliminarFacturaAnuladaProps) => {
  const [abierto, setAbierto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esAdministrador = ConfigUtils.getUserRole() === constantes.ROLES.ADMINISTRADOR;
  if (!esAdministrador || !factura || factura.estado !== "Anulada" || factura.numero) return null;

  const cerrar = () => {
    setAbierto(false);
    setError(null);
  };

  const eliminar = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await FacturacionService.eliminarFactura(factura._id);
      setAbierto(false);
      onEliminada(response.data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se ha podido eliminar la factura");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Eliminar el borrador anulado y liberar el servicio">
        <Button size="small" variant="outlined" color="error" onClick={() => setAbierto(true)}>
          Eliminar
        </Button>
      </Tooltip>

      {abierto && (
        <Dialog open={abierto} onClose={loading ? undefined : cerrar} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>Eliminar factura</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Se eliminará definitivamente este borrador anulado. No se puede deshacer
            </Alert>
            {factura.tipo !== "manual" && (
              <Typography variant="body2">
                {factura.facturaOriginalId
                  ? "La factura original volverá a estar activa"
                  : "El servicio quedará sin factura y podrá facturarse de nuevo"}
              </Typography>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={cerrar} disabled={loading}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={eliminar} disabled={loading}>
              {loading ? "Eliminando..." : "Eliminar factura"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default EliminarFacturaAnulada;
